import React, { useState, useEffect } from 'react';
import {
    Button,
    Row,
    Col,
    InputGroup,
    Badge,
    Card
} from 'react-bootstrap';
import axios, { all } from 'axios';
import { checkGmailStatus, connectGmail, sendGmailEmail, disconnectGmail } from '../../../../helpers'; 
import { getAccountsByUser, createAccount, deleteAccount, getOrganizationsByUser, getAccountsByOrganization } from '../../../../redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import { FormInput } from '../../../../components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import classNames from 'classnames';
import config from '../../../../config';

const apiUrl = process.env.REACT_APP_API_URL

const EmailSetup = () => {
    const dispatch = useDispatch();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [gmailConnected, setGmailConnected] = useState(false);
    const [form, setForm] = useState({ to: '', subject: '', message: '' });
    const [message, setMessage] = useState('')
    const [newEmail, setNewEmail] = useState('');
    const [selectedOrganization, setSelectedOrganization] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);

  
    // const user = JSON.parse(sessionStorage.getItem('Zicarta_user'));
    const { user, tokens, userId, accounts, organizations } = useSelector((state) => ({
      user: state.Auth.user?.user ?? {},
      tokens: state.Auth.user?.tokens ?? {},
      userId: state.Auth.user?.user?.id ?? null,
      accounts: state.EmailAccounts?.data?.results ?? [],
      organizations: state.Organizations?.data ?? [],
    }));

    //Declare variable for all accounts
    const [allAccounts, setAllAccounts] = useState([]);

    // Fetch organizations once
    useEffect(() => {
      if (!userId) return;
      dispatch(getOrganizationsByUser(userId));
    }, [userId, dispatch]);
    
    //Fetch accounts by user and combine into all accounts
    useEffect(() => {
      if (!userId) return;
    
      let cancelled = false;
    
      const fetchAllAccounts = async () => {
        try {
          // 1. Fetch user accounts
          await dispatch(getAccountsByUser(userId));
        } catch (err) {
          console.error("Failed to fetch user accounts:", err);
        }
      };
    
      fetchAllAccounts();
    
      return () => {
        cancelled = true;
      };
    }, [userId, dispatch]);

    //Fetch accounts by organization & merge with user accounts.
    useEffect(() => {
      if (!accounts?.length && !organizations?.length) return;

      let cancelled = false;
    
      const fetchOrgAccounts = async () => {
        try {
          // Fetch organization accounts via Axios
          const orgAccountsResponses = await Promise.all(
            organizations.map((org) =>
              axios.get(`${config.server.api}/v1/organizations/${org.id}/accounts`, {
                headers: {
                  Authorization: `Bearer ${tokens.access.token}`,
                },
              })
            )
          );
    
          const orgAccounts = orgAccountsResponses.flatMap(
            (res) => res.data?.results ?? []
          );
    
          // Combine and deduplicate based on _id
          const combined = [...accounts, ...orgAccounts];
          const deduped = Array.from(
            new Map(combined.map((acc) => [acc.id, acc])).values()
          );
    
          // Filter out archived/deleted
          const activeAccounts = deduped.filter(
            (acc) => !acc.isDeleted && !acc.isArchived
          );
    
          if (!cancelled) setAllAccounts(activeAccounts);
        } catch (err) {
          console.error("Failed to fetch all accounts:", err);
        }
      };
    
      fetchOrgAccounts();
    
      return () => {
        cancelled = true;
      };
    }, [userId, accounts, organizations, tokens.access.token]);    
      
    const selectedAccount = allAccounts[selectedIndex] ?? {};
    const bearerToken = tokens?.access?.token;

    useEffect(() => {
      setGmailConnected(false); // reset before checking new account
    }, [selectedIndex]);    

    //Check if Gmail is connected
    useEffect(() => {
      if (!bearerToken || !selectedAccount?.id) return;
    
      const params = {
        headers: { Authorization: `Bearer ${bearerToken}` },
        account: selectedAccount,
      };
    
      checkGmailStatus(params)
        .then((res) => {
          const isConnected = res?.data?.connected === true;
          setGmailConnected(isConnected);
        })
        .catch((err) => {
          console.error("Error checking Gmail status:", err.response || err);
          setGmailConnected(false);
        });
    }, [bearerToken, selectedAccount?.id]);    

    useEffect(()=> {
      setForm({ 
          ...form, 
          to: user.email,
          subject: "Test Email - Zicarta Integration",
          // message: "This is a test email. Your integration is working. \n - Zicarta"
      })
      setMessage(
        "This is a test email. Your integration is working. \n - Zicarta"
      )
    }, [user])
      
      
    const createEmailMime = (to, subject, htmlContent) => {
      const encodedMessage = [
          'Content-Type: text/html; charset=UTF-8',
          'MIME-Version: 1.0',
          `To: ${to}`,
          `Subject: ${subject}`,
          '',
          htmlContent
      ].join('\n');

      return btoa(unescape(encodeURIComponent(encodedMessage)))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
    };

    /** Connect to Gmail OAuth for the selected account */
    const handleGmailConnect = async () => {
      const selected = selectedAccount;
      if (!selected?.address) return alert('Select or add an email first.');

      try {
        const result = await connectGmail({ userId, address: selected.address });
        const token = result.data.token;

        window.open(`${apiUrl}/v1/gmail/oauth?token=${token}`, '_blank');
      } catch (err) {
        console.error('Error connecting to Gmail:', err);
      }
    };

    const handleSend = async () => {
        try {
            const emailArray = form.to.split(',').map((email) => email.trim());
            const recipient = emailArray[0]; // Gmail API only supports one `To` address per message

            const rawMessage = createEmailMime(
                recipient,
                form.subject,
                message // this should be HTML (with image tags etc)
            );
    
            const res = await sendGmailEmail({
                raw: rawMessage,
                headers: { Authorization: `Bearer ${bearerToken}` },
                email: selectedAccount?.address,
            });
    
            alert(res.message);
        } catch (error) {
            console.error('Error sending email:', error);
            alert('Failed to send email.');
        }
    };    

    const handleGmailDisconnect = async () => {
        const params = {
            headers: {
                Authorization: `Bearer ${bearerToken}`,
            },
            email: selectedAccount.address,
        };
    
        try {
            await disconnectGmail(params);
            setGmailConnected(false);
            alert('Disconnected from Gmail');
        } catch (err) {
            console.error('Failed to disconnect Gmail:', err);
            alert('Error disconnecting from Gmail');
        }
    };  

    //Add new email account
    const handleAddEmailSubmit = async () => {
      if (!newEmail) return alert('Please enter an email address.');
      const newItem = { address: newEmail, user: userId, organization: selectedOrganization };
  
      try {
        await dispatch(createAccount(newItem));
        await dispatch(getAccountsByUser(userId));
        setShowAddForm(false);
        setNewEmail('');
        setSelectedIndex(accounts.length); // Select newly added
      } catch (err) {
        console.error('Failed to add Gmail account:', err.response || err);
      }
    };

    const handleDeleteEmail = async () => {
      if (!selectedAccount?.id) return alert('No email selected.');
      if (!window.confirm(`Are you sure you want to delete ${selectedAccount.address}?`)) return;
    
      try {
        await dispatch(deleteAccount(selectedAccount));
        alert('Email deleted successfully.');
    
        // Refresh accounts list and reset selection
        await dispatch(getAccountsByUser(userId));
        setSelectedIndex(0);
      } catch (err) {
        console.error('Failed to delete email account:', err.response || err);
        alert('Error deleting email account.');
      }
    };
  
    return (
      <div className="max-w-lg mx-auto p-4 border rounded shadow">
        <h2 className="text-xl font-bold mb-4">Gmail Integration</h2>

        {/* Gmail Account Selector */}
        <Card>
          <Card.Header><h3>Select Gmail Account:</h3></Card.Header>
          <Card.Body>
            <div className="mb-3">
              <Row>
                  <Col lg="7" >
                      <InputGroup>
                        <FormInput
                          // label=""
                          type="select"
                          value={selectedIndex}
                          onChange={(e) => setSelectedIndex(Number(e.target.value))}
                          containerClass=""
                          className="form-select"
                        >
                          {allAccounts?.length === 0 && <option>No Gmail accounts</option>}
                          {allAccounts?.map((acc, idx) => (
                            <option key={idx} value={idx}>
                              {acc.address || `Account ${idx + 1} (Not Connected)`}
                            </option>
                          ))}
                        </FormInput>
                        <Button onClick={handleDeleteEmail} className="btn-danger uil-trash" />
                      </InputGroup>
                  </Col>
                </Row>
                <Row className='mt-3'>
                  <Col lg="1" md="1">
                    <h4>
                      Status: 
                    </h4>
                  </Col>
                  <Col>
                  {gmailConnected ? (
                    <Col lg={3}>
                      <Badge pill bg="success">
                        Connected
                      </Badge>
                    </Col>
                  ) : (
                    <Col lg={3}>
                      <Badge pill bg="warning">
                        Not Connected
                      </Badge>
                    </Col>
                  )}
                  </Col>
                </Row>
            </div>
          <div className="mb-4">
            {gmailConnected ? (
              <>
                <Row>
                  <Col>
                    <Button onClick={handleGmailDisconnect} className="bg-red-500 text-white px-4 py-2 rounded mr-2">
                      Disconnect Gmail
                    </Button>
                  </Col>
                </Row>
              </>
            ) : (
              <Row>
                <Col lg='2' md='3' >
                  <Button onClick={() => handleGmailConnect(userId)} className="btn-primary">
                    Connect Gmail
                  </Button>
                </Col>
              </Row>
            )}
          </div>
        </Card.Body>
      </Card>
      {gmailConnected && (
        <Card>
          <Card.Header><h3>Email Input Form</h3></Card.Header>
          <Card.Body>
            <form onSubmit={handleSend} className="space-y-3 mb-4">
              <Row className="d-flex justify-content-center">
                  <p className="h4">To:</p>
                  <input type="email" placeholder="To" required
                  className="w-full border p-2 rounded"
                  value={form.to}
                  onChange={(e) => setForm({ ...form, to: e.target.value })}
                  />
              </Row>
              <Row className="d-flex justify-content-center mt-4">
                  <p className="h4">Subject:</p>
                  <input type="text" placeholder="Subject" required
                      className="w-full border p-2 rounded"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  />
              </Row>
              <Row className="d-flex justify-content-center mt-4">
                <Col>
                    <p className="h4">Message:</p>
                    <ReactQuill
                        theme="snow"
                        value={message}
                        onChange={setMessage}
                        className="mt-2"
                        placeholder="This is a test email. Your integration is working. \n - Zicarta"
                    />
                  </Col>
              </Row>
              <Row className="d-flex justify-content-center mt-4">
                  <Col>
                      <Button type="submit" className="bg-green-600 text-white px-4 py-2 rounded">
                          Send Email
                      </Button>
                  </Col>
              </Row>
            </form>
          </Card.Body>
        </Card>
      )}   



        {/* Add Email Button and Form */}
        <Card className="mb-4">
          <Card.Header>
            <h3>
              Connect a New Email
            </h3>
          </Card.Header>
          <Card.Body>
            {!showAddForm ? (
              <Button onClick={() => setShowAddForm(true)} variant='success'>
                + Add Email
              </Button>
            ) : (
              <InputGroup>
                <Col>
                  <Row className="d-flex justify-content-center mt-2">
                    <Col>
                      <input
                        type="email"
                        placeholder="Enter Gmail address"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="w-full border p-2 rounded"
                      />
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-center mt-2">
                    <Col>
                      <select
                        value={selectedOrganization ?? "none"}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedOrganization(val === "none" ? null : val);
                        }}
                        className="border p-2 rounded w-full mt-3"
                      >
                        {organizations?.length === 0 && <option>No organizations</option>}

                        <option value="none">Not associated with an org</option>

                        {organizations?.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-center mt-4">
                    <Col>
                      <Button onClick={handleAddEmailSubmit} className="bg-green-500 text-white px-4 py-2 rounded">
                        Save Email
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </InputGroup>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }

export default EmailSetup;